import type { JSX } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Entity } from '../api.js';
import { t } from '../i18n.js';
import { formatDate, formatMonth, stateWithReason } from '../format.js';
import {
  NARROW_COLUMNS,
  WIDE_MIN_TILE,
  dateOf,
  duplicateCounts,
  emojisOf,
  excerptOf,
  gridDensity,
  items,
  type MonthGroup,
  picked,
  scoreOf,
  selection,
  togglePicked,
  view,
} from '../state.js';
import { narrow } from './media.js';
import { onOpen } from '../actions.js';
import { MarkdownExcerpt } from '../markdown.js';
import { ObjectMenu } from './menu.js';
import { knownUrl, refusedUrl, urlFor } from '../thumbs.js';
import { IconAlert, IconDuplicate, IconExposed, IconNoImage, IconPending, IconPlay, IconStarFilled } from './icons.js';

/**
 * **Mes fichiers** — *qu'est-ce que j'ai ?*
 *
 * Elle porte **tous les types** depuis 4.5, et non les seuls médias. Une note et
 * un document sont des fichiers ; les laisser hors de la grille obligeait à
 * savoir d'avance dans quel écran chercher — le préalable que le rangement par
 * question devait justement supprimer. Ce qui n'y figure pas, ce sont les
 * collections : une collection n'est pas un fichier, elle vit dans les partages.
 *
 * Ce qui n'a pas de vignette en reçoit une **qui dit quelque chose** : l'extrait
 * d'une note, rendu en Markdown, et le type pour un document. Un mur d'icônes
 * identiques aurait été le degré zéro — on saurait qu'il y a douze notes sans
 * pouvoir en reconnaître une seule.
 *
 * Deux choses la distinguent d'une grille ordinaire, et toutes deux viennent du
 * socle plutôt que du goût :
 *
 * ① **Elle est virtualisée.** Une photothèque réelle, ce sont des centaines de
 *    médias. Monter mille `<img>` coûte la mémoire du navigateur, mais surtout :
 *    chacune demanderait son URL présignée, et deux cents demandes contre un stage
 *    étranglé à 20 req/s ne peignent rien du tout (D-22). La virtualisation est
 *    donc ce qui **borne le lot** de `POST /files/urls`, pas seulement ce qui
 *    allège le DOM.
 * ② **Elle est groupée par mois**, parce que c'est la question qu'on lui pose. Le
 *    groupement disparaît dès que le tri n'est plus chronologique : un en-tête de
 *    mois qui réapparaît toutes les trois tuiles ne dit plus rien.
 *
 * Les rangées ont une **hauteur mesurée**, pas devinée : la légende d'une tuile
 * dépend de la police et du zoom, et une constante en dur ferait dériver la
 * position de défilement d'autant plus qu'on descend.
 *
 * ### Les positions passent par un `style` **objet**, et c'est une contrainte de CSP
 *
 * La CSP du site est `style-src 'self'`, **sans `'unsafe-inline'`** : elle refuse
 * donc aussi l'attribut `style` — c'est pour cela que l'indentation de l'arbre
 * (`space.tsx`) passe par un `data-depth` énumérable en CSS.
 *
 * Ici, ce n'est pas énumérable : la position d'une rangée dépend du défilement. Le
 * `style` est donc passé **en objet** et non en chaîne, ce qui fait écrire Preact
 * par le CSSOM (`el.style.top = …`) plutôt que par `setAttribute('style', …)` — et
 * le CSSOM n'est pas couvert par la CSP. La distinction n'est pas théorique : la
 * même valeur en chaîne serait bloquée, la grille s'effondrerait en pile, et le
 * seul indice serait une ligne de console. Un parcours écoute donc les violations
 * de CSP pendant le rendu de la grille.
 */

/** Ce qu'on garde au-delà de la fenêtre visible, en haut comme en bas. */
const OVERSCAN = 2;

const GAP = 12;

/** Doit rester en accord avec la `height` de `.photo-head`. */
const HEAD_HEIGHT = 40;

type Row =
  | { readonly kind: 'head'; readonly key: string; readonly count: number }
  | { readonly kind: 'tiles'; readonly key: string; readonly items: readonly Entity[] };

/**
 * **Le contenu d'une collection n'a plus d'écran à lui** *(2026-08-14)*.
 *
 * `GalleryView` vivait ici : une grille, un avertissement, et rien d'autre — ni
 * filtres, ni bascule lignes/vignettes, ni densité. C'était le dernier endroit où
 * les mêmes objets recevaient un corps différent selon le lieu, et le retour
 * d'usage l'a relevé : « l'affichage d'un partage est différent de l'affichage
 * dans mon espace ». Une collection se peint désormais par `SpaceView`, comme un
 * dossier ; ce qu'elle a en propre — retirer n'est pas supprimer — y tient en une
 * phrase conditionnée à la vue.
 */

/**
 * **La grille reçoit ses groupes, elle ne va plus les chercher.**
 *
 * Elle lisait `months` directement, ce qui la liait à la question de la
 * photothèque — *quand ?*. Un dossier pose une autre question — *où ?* — et n'a
 * pas d'en-têtes de mois. Le paramètre suffit à servir les deux : un groupe dont
 * la clé est vide ne dessine pas d'en-tête, et c'était déjà le cas.
 */
export function VirtualGrid({
  groups,
}: {
  readonly groups: readonly MonthGroup[];
}): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(1);
  const [rowHeight, setRowHeight] = useState(220);
  const [window, setWindow] = useState({ top: 0, height: 800 });

  /**
   * Les rangées, à plat. Un en-tête et une rangée de tuiles ont des hauteurs
   * différentes ; les mélanger dans une seule liste indexée est ce qui permet de
   * calculer une position de défilement sans parcourir toutes les photos.
   */
  const rows = useMemo<readonly Row[]>(() => {
    const out: Row[] = [];
    for (const group of groups) {
      if (group.key) out.push({ kind: 'head', key: group.key, count: group.items.length });
      for (let i = 0; i < group.items.length; i += cols) {
        out.push({
          kind: 'tiles',
          key: `${group.key}#${i}`,
          items: group.items.slice(i, i + cols),
        });
      }
    }
    return out;
  }, [groups, cols]);

  // L'en-tête de mois a une hauteur **fixée par la CSS** (`.photo-head`), donc
  // connue : une seule ligne de texte, sans habillage. Seule la rangée de tuiles
  // dépend de la largeur, et c'est elle qu'on mesure.
  const heightOf = (row: Row): number => (row.kind === 'head' ? HEAD_HEIGHT : rowHeight);

  /** Les décalages cumulés — un seul parcours, et il sert au rendu comme à la mesure. */
  const offsets = useMemo(() => {
    const out: number[] = [0];
    for (const row of rows) out.push(out[out.length - 1]! + heightOf(row));
    return out;
  }, [rows, rowHeight]);

  const total = offsets[offsets.length - 1] ?? 0;

  // **Le nombre de colonnes vient de la largeur mesurée**, jamais d'un point de
  // rupture : la même grille vit dans une fenêtre plein écran et dans une colonne
  // rétrécie par l'inspecteur, et une media query ne connaît pas la seconde.
  //
  // **La densité y entre depuis le 2026-08-09**, et les deux tailles d'écran ne
  // l'expriment pas de la même façon. Sur un téléphone, la demande est un
  // *nombre* — 1, 3 ou 5 en largeur — et il n'y a qu'une largeur possible, donc
  // rien à adapter. Sur un grand écran, un nombre fixe donnerait des vignettes de
  // six cents pixels de côté : la densité y est donc une **largeur minimale**, et
  // le nombre de colonnes s'en déduit, comme avant.
  const density = gridDensity.value;
  const isNarrow = narrow.value;
  useLayoutEffect(() => {
    const node = host.current;
    if (!node) return;
    const measure = (): void => {
      if (isNarrow) return setCols(NARROW_COLUMNS[density]);
      const width = node.clientWidth;
      setCols(Math.max(1, Math.floor((width + GAP) / (WIDE_MIN_TILE[density] + GAP))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [density, isNarrow]);

  // La hauteur d'une rangée est **relue sur la première rangée rendue**. Elle
  // dépend de la largeur, de la police et du zoom ; la figer en constante ferait
  // dériver la position de défilement, et la dérive croît avec la distance.
  useLayoutEffect(() => {
    const node = probe.current;
    if (!node) return;
    const measure = (): void => {
      const seen = node.getBoundingClientRect().height;
      if (seen > 0 && Math.abs(seen - rowHeight) > 1) setRowHeight(seen);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [cols, rowHeight]);

  /**
   * **La fenêtre visible se mesure contre le *viewport*, pas contre un conteneur.**
   *
   * La première version cherchait « l'ancêtre scrollable » — le premier parent en
   * `overflow-y: auto`. C'était la mauvaise question, et elle a une bonne réponse
   * fausse : `.view` **porte** `overflow-y: auto` mais ne défile jamais, faute de
   * hauteur contrainte — elle grandit avec son contenu, et c'est la page qui
   * défile. La grille se croyait donc dans une fenêtre de 4 500 px, montait ses
   * cinquante rangées, et **la virtualisation ne virtualisait plus** sans que rien
   * ne le dise à l'écran.
   *
   * `getBoundingClientRect()` est toujours relatif au viewport : `-box.top` donne
   * la position du haut de l'écran dans le repère de la grille, que le défilement
   * vienne de la page ou d'un conteneur. Un `scroll` ne remonte pas, d'où la
   * capture au niveau du document — un conteneur intercalé plus tard continuera
   * de fonctionner sans qu'on ait à le prévoir.
   */
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const onScroll = (): void => {
      const box = node.getBoundingClientRect();
      setWindow({ top: -box.top, height: globalThis.innerHeight });
    };
    onScroll();
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    globalThis.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      globalThis.removeEventListener('resize', onScroll);
    };
  }, [total]);

  const first = Math.max(0, indexAt(offsets, window.top) - OVERSCAN);
  const last = Math.min(rows.length, indexAt(offsets, window.top + window.height) + 1 + OVERSCAN);
  const visible = rows.slice(first, last);
  const albumId = view.value.kind === 'collection' ? view.value.aid : undefined;

  // **La sonde va sur la première rangée de tuiles, où qu'elle soit** — pas sur
  // l'indice 0. Le groupement met un en-tête de mois en tête de liste : accrochée
  // à l'indice 0, la sonde ne mesurait jamais rien, la hauteur restait à
  // l'estimation, et la grille se déclarait plus courte qu'elle n'est. Tout
  // tenait alors dans la fenêtre — c'est-à-dire que **la virtualisation ne
  // virtualisait plus**, et rien ne le disait à l'écran.
  const probeAt = visible.findIndex((row) => row.kind === 'tiles');

  return (
    <div
      /* `picking` : **une sélection est en cours**, donc les cases restent
         visibles sur toutes les tuiles. Sans elle, cocher la première ferait
         disparaître les autres cases jusqu'au survol — on cocherait à l'aveugle. */
      class={picked.value.size > 0 ? 'photo-grid picking' : 'photo-grid'}
      data-testid="gallery"
      /* La densité descend en attribut : au pas le plus serré, une légende de
         deux lignes sous une vignette de soixante pixels n'est plus lisible — la
         grille devient une planche-contact, et c'est ce qu'on lui demande. */
      data-density={density}
      data-rows={rows.length}
      data-visible={visible.length}
      ref={host}
      style={{ height: `${total}px`, position: 'relative' }}
    >
      {visible.map((row, i) => (
        <div
          key={row.key}
          ref={i === probeAt ? probe : undefined}
          class={row.kind === 'head' ? 'photo-head' : 'photo-row'}
          style={{
            position: 'absolute',
            top: `${offsets[first + i]}px`,
            left: 0,
            right: 0,
            ...(row.kind === 'tiles'
              ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }
              : {}),
          }}
        >
          {row.kind === 'head' ? (
            <h3 data-testid={`month-${row.key}`}>
              {formatMonth(row.key)} <span class="photo-count">{row.count}</span>
            </h3>
          ) : (
            row.items.map((entity) =>
              entity.Fid ? (
                <Tile key={entity.Fid} entity={entity} fid={entity.Fid} album={albumId} />
              ) : null,
            )
          )}
        </div>
      ))}
    </div>
  );
}

/** Le premier index dont le **bas** dépasse `y`. Recherche binaire : `offsets` croît. */
function indexAt(offsets: readonly number[], y: number): number {
  let lo = 0;
  let hi = offsets.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid + 1]! <= y) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * **L'appui long, et pourquoi il remplace la case permanente.** *(Exporté depuis
 * le 2026-08-15 : les lignes s'en servent aussi — une présentation ne décide pas
 * des gestes qu'on peut faire sur un objet.)*
 *
 * Une case à cocher posée sur chaque vignette est une pièce de chrome sur ce que
 * la page est censée montrer : elle sert à un geste rare et coûte un coin d'image
 * à chaque tuile, en permanence. Sur un écran qui a un survol, elle peut
 * s'effacer et reparaître — sur un écran tactile, il n'y a pas de survol, donc
 * pas de retour possible : elle y restait allumée tout le temps. Le geste que
 * tout le monde connaît sur mobile est l'appui long, et c'est déjà celui de l'app.
 *
 * Trois précautions, et chacune répare un défaut qu'on ne voit qu'à l'usage :
 * le glissement **annule** (sinon un défilement sélectionne), le clic qui suit
 * l'appui est **avalé** (sinon on sélectionne *et* on ouvre), et le menu
 * contextuel du navigateur est **retenu** (sinon il s'ouvre par-dessus).
 *
 * ### Le menu du navigateur était retenu trop tard *(2026-08-15)*
 *
 * Il ne l'était que si notre propre appui avait **déjà** abouti : `fired.current`
 * était la condition. Or Chrome sur Android ouvre son menu d'image — « ouvrir
 * dans un nouvel onglet », « télécharger l'image » — au terme de son propre délai,
 * qui vaut à peu près le nôtre. Selon lequel des deux minuteurs tombait en
 * premier, la sélection s'ouvrait *et* le menu par-dessus : « un appui long sur
 * une vignette déclenche bien la sélection mais le navigateur capture aussi
 * l'événement ».
 *
 * Le geste est retenu **dès qu'il vient d'un doigt**, sans attendre de savoir si
 * le nôtre a réussi : sur une tuile, l'appui long est le geste de la sélection, et
 * il n'y en a pas d'autre à lui disputer. Le clic droit de la souris, lui, garde
 * le menu du navigateur — il n'entre en concurrence avec rien.
 *
 * Et le délai passe **sous** celui du navigateur (400 ms contre 500) : Chrome
 * accompagne son menu d'un `pointercancel`, et un `pointercancel` annule notre
 * minuteur. Arriver après lui, c'était n'arriver jamais.
 */
const LONG_PRESS_MS = 400;
const LONG_PRESS_SLOP = 10;

export function useLongPress(onLongPress: () => void): {
  readonly fired: { current: boolean };
  readonly handlers: Record<string, (ev: never) => void>;
} {
  const timer = useRef<number | undefined>(undefined);
  const fired = useRef(false);
  const touch = useRef(false);
  const origin = useRef<{ x: number; y: number } | undefined>(undefined);

  const cancel = (): void => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  };

  return {
    fired,
    handlers: {
      onPointerDown: (ev: PointerEvent) => {
        touch.current = ev.pointerType !== 'mouse';
        // Un clic droit n'est pas un appui long, et le bouton du milieu non plus.
        if (ev.pointerType === 'mouse' && ev.button !== 0) return;
        // **Les contrôles de la tuile ne sont pas la tuile.** S'attarder une
        // demi-seconde sur la case ou sur le menu déclencherait la sélection
        // *puis* le geste du contrôle : deux effets pour un doigt posé.
        if ((ev.target as HTMLElement | null)?.closest?.('.tile-pick, .row-pick, .obj-actions')) {
          return;
        }
        fired.current = false;
        origin.current = { x: ev.clientX, y: ev.clientY };
        timer.current = window.setTimeout(() => {
          fired.current = true;
          onLongPress();
        }, LONG_PRESS_MS);
      },
      onPointerMove: (ev: PointerEvent) => {
        const from = origin.current;
        if (!from) return;
        if (Math.hypot(ev.clientX - from.x, ev.clientY - from.y) > LONG_PRESS_SLOP) cancel();
      },
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerCancel: cancel,
      onContextMenu: (ev: Event) => {
        if (touch.current || fired.current) ev.preventDefault();
      },
    } as Record<string, (ev: never) => void>,
  };
}

function Tile({
  entity,
  fid,
  album,
}: {
  readonly entity: Entity;
  readonly fid: string;
  readonly album: string | undefined;
}): JSX.Element {
  // **Le drapeau de présence est `size`, pas `State`** : une entité peut être
  // `READY` pour sa source et n'avoir pas encore sa vignette. Demander l'URL d'un
  // rôle sans octets constatés la ferait omettre du lot, pas afficher une image.
  const ready = entity.Variants?.['thumb']?.size !== undefined;
  // Un type qui **ne déclare aucune vignette** n'est pas en attente : il n'en
  // aura jamais. Confondre les deux afficherait un sablier perpétuel sur chaque
  // note — l'utilisateur attendrait une image qui ne vient pas.
  const thumbable = entity.Variants?.['thumb'] !== undefined;
  const chosen = picked.value.has(fid);
  const picking = picked.value.size > 0;
  const longPress = useLongPress(() => togglePicked(fid));

  return (
    <figure
      class={[
        'tile',
        selection.value === fid ? 'selected' : '',
        chosen ? 'picked' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`tile-${fid}`}
      {...longPress.handlers}
    >
      <label class="tile-pick" title={t('gallery.pick')}>
        <input
          type="checkbox"
          data-testid={`pick-${fid}`}
          checked={chosen}
          onChange={() => togglePicked(fid)}
        />
      </label>
      <button
        type="button"
        class="tile-open"
        data-testid={`open-${fid}`}
        aria-label={entity.Name}
        onClick={() => {
          // L'appui long vient de sélectionner : le clic que le navigateur émet
          // derrière ouvrirait la chose qu'on voulait seulement cocher.
          if (longPress.fired.current) {
            longPress.fired.current = false;
            return;
          }
          // **Une sélection en cours change ce que vaut un appui simple** — c'est
          // la convention de toutes les galeries, et déjà celle de l'app : tant
          // qu'on coche, on coche ; on ne part pas en plein écran parce qu'on a
          // visé la sixième photo.
          if (picking) return togglePicked(fid);
          onOpen(entity, items.value);
        }}
      >
        {ready ? (
          <Thumb fid={fid} alt={entity.Name} strip={filmstripOf(entity)} />
        ) : thumbable ? (
          <div
            class="tile-placeholder"
            data-testid={`pending-${fid}`}
            // **L'infobulle porte la cause** *(2026-09-12)* : c'est le premier
            // endroit où l'on cherche pourquoi une tuile est barrée, et « échec du
            // traitement » y était un cul-de-sac. La fiche en dit plus et offre la
            // reprise ; la tuile doit au moins nommer le motif.
            title={
              entity.State === 'FAILED'
                ? stateWithReason('FAILED', entity.FailReason)
                : t('entity.processing')
            }
          >
            {/* **Des icônes du jeu, et non des caractères.** `⚠` et `⏳` sortaient
                en emoji couleur sur la plupart des systèmes, au milieu de traits
                monochromes — et en rectangle vide là où la police manquait. */}
            {entity.State === 'FAILED' ? <IconAlert size={24} /> : <IconPending size={24} />}
          </div>
        ) : (
          <Preview entity={entity} fid={fid} />
        )}
        {/* **Le signe « c'est une vidéo »** *(2026-08-15)*. La pellicule ne se
            lit comme un film que quand on sait déjà que c'en est un : une case
            de vidéo est une image fixe comme une autre. Le triangle est au
            **centre** parce que les quatre coins sont pris — sélection,
            exposition, réactions — et parce que c'est là que toutes les grilles
            le posent. Il n'annonce **pas** de durée : on ne la stocke pas. */}
        {entity.Kind === 'VID' ? (
          <span class="tile-play" data-testid={`play-${fid}`} aria-hidden="true">
            <IconPlay size={22} />
          </span>
        ) : null}
        <TileBadges entity={entity} fid={fid} />
      </button>
      {/* **Le menu est en face du nom**, pas sous la légende : posé en dernier
          dans une colonne, il flottait au milieu du blanc d'une tuile plus haute
          que ses voisines, et ne se lisait plus comme appartenant à l'objet. */}
      <figcaption>
        <span class="tile-text">
          {/* Le nom est **tronqué à deux lignes** par la CSS — un nom d'appareil
              photo en fait six, et la grille virtualisée n'a qu'une hauteur de
              rangée. L'infobulle rend le nom entier au survol. */}
          <span class="tile-name" title={entity.Name} data-testid={`item-name-${fid}`}>
            {entity.Name}
          </span>
          <span class="tile-meta">
            {/* La note et les emoji ont quitté la légende pour **l'image**
                *(2026-08-09)* : posés sous le nom, ils étaient une ligne de texte
                de plus qu'on ne lit qu'en s'arrêtant, et la grille ne disait rien
                de ce qui a été aimé tant qu'on ne l'inspectait pas. */}
            <span class="tile-date">{formatDate(dateOf(entity))}</span>
          </span>
        </span>
        {/* **La sélection multiple s'ajoute, elle ne remplace rien.** Le bouton
            de suppression par tuile était là depuis l'étape 3 : l'avoir retiré au
            profit de la barre de sélection a fait tomber trois parcours d'origine
            — et c'est bien ce qu'ils sont là pour attraper. Supprimer une photo
            qu'on regarde ne doit pas demander de la cocher d'abord.
            Depuis 4.5, ces actions ne sont plus définies ici : c'est le **menu
            d'objet unique**, le même que dans l'arborescence et l'inspecteur. */}
        <ObjectMenu entity={entity} id={fid} collection={album} />
      </figcaption>
    </figure>
  );
}

/**
 * **L'aperçu de ce qui n'aura jamais de vignette.**
 *
 * Une note rend son `Excerpt` — les premières lignes, déjà aplaties de leur
 * Markdown par le serveur, et **projetées sur les arêtes** : la même tuile se
 * peint donc dans un feed de groupe sans une lecture de plus. Le calculer côté
 * client aurait demandé une requête S3 par tuile montée, pour deux lignes de
 * texte : exactement la N+1 que le lot de présignatures a supprimée en 4.4.
 *
 * Un document n'a pas d'aperçu et n'en aura pas : son descripteur ne déclare
 * aucun dérivé, une vignette de PDF demandant un moteur de rendu que rien dans le
 * socle ne suppose. Il montre donc son extension, qui est l'information utile.
 *
 * L'extrait passe par le rendu Preact et non par un `innerHTML` : le texte vient
 * d'une note, donc d'un utilisateur, et une note ne doit jamais pouvoir injecter
 * de HTML dans la page qui la lit — c'est le parcours de 4.3 qui le tient.
 *
 * **Il est rendu, plus aplati** (4.5 bis). Le serveur l'avait dépouillé de sa
 * syntaxe : une liste de courses arrivait en une phrase, un titre en texte gris,
 * et la tuile ne ressemblait plus à la note qu'elle annonce. Le moteur est celui
 * du lecteur, en **mode inerte** — l'extrait vit dans un bouton, et ni un lien ni
 * une case à cocher ne peuvent y habiter.
 */
function Preview({
  entity,
  fid,
}: {
  readonly entity: Entity;
  readonly fid: string;
}): JSX.Element {
  const excerpt = excerptOf(entity);
  if (excerpt) {
    return (
      <div class="tile-excerpt paper">
        <MarkdownExcerpt source={excerpt} testId={`excerpt-${fid}`} />
      </div>
    );
  }
  const ext = entity.Variants?.['source']?.ext ?? entity.Type.toLowerCase();
  return (
    <div class="tile-placeholder tile-kind" data-testid={`kind-${fid}`} title={entity.Name}>
      <span class="tile-ext">{ext.toUpperCase()}</span>
    </div>
  );
}

/**
 * **Ce qu'une tuile dit sans qu'on l'ouvre** — les réactions, et l'exposition.
 *
 * C'est le retour d'usage du 2026-08-09 : « il n'y a pas d'overlay pour indiquer
 * les réactions et les partages ». Les deux étaient pourtant transportés depuis
 * l'étape 1 — le premier en légende, où il se lit mal ; le second nulle part.
 *
 * **Aucune requête de plus.** `reactions` est agrégé par le serveur à la réponse
 * et `authGroups` est déjà sur l'item — filtré au point de vue de l'appelant, si
 * bien qu'un membre n'y voit que les cercles qu'il partage avec le propriétaire.
 * Les peindre ne coûte donc rien ; c'est ne pas les peindre qui coûtait, en
 * inspections successives pour retrouver ce qui a été partagé.
 *
 * Deux coins, deux natures : ce que **les autres** ont posé en bas à gauche, ce
 * qui **est sorti** en haut à droite, dans la teinte `--share` qui n'est ni
 * l'action ni l'avertissement. La seule teinte chaude de la table, au milieu de
 * neutres froids : elle ne se confond avec rien.
 */
function TileBadges({
  entity,
  fid,
}: {
  readonly entity: Entity;
  readonly fid: string;
}): JSX.Element | null {
  const score = scoreOf(entity);
  const emojis = emojisOf(entity);
  const exposed = entity.authGroups?.length ?? 0;
  // **Le doublon probable** *(K4)* — le dernier coin libre, en bas à droite. Il dit
  // combien de photos compte le groupe : « 2 », c'est une copie ; « 7 », une rafale
  // ou un seuil réglé large.
  const twins = duplicateCounts.value.get(fid) ?? 0;

  if (score === undefined && emojis.length === 0 && exposed === 0 && twins === 0) return null;

  return (
    <>
      {score !== undefined || emojis.length > 0 ? (
        <span class="tile-badge reactions">
          {score !== undefined ? (
            <span class="tile-score" data-testid={`score-${fid}`} title={t('reaction.average')}>
              <IconStarFilled size={12} />
              {score.toFixed(1)}
            </span>
          ) : null}
          {emojis.length > 0 ? (
            <span class="tile-emojis" data-testid={`emojis-${fid}`}>
              {emojis.join('')}
            </span>
          ) : null}
        </span>
      ) : null}

      {twins > 0 ? (
        <span
          class="tile-badge duplicate"
          data-testid={`duplicate-${fid}`}
          title={t('gallery.duplicateOf', { count: twins })}
        >
          <IconDuplicate size={12} />
          {twins}
        </span>
      ) : null}

      {exposed > 0 ? (
        <span
          class="tile-badge exposed"
          data-testid={`exposed-${fid}`}
          title={t('share.exposedTo', { count: exposed })}
        >
          <IconExposed size={12} />
          {exposed > 1 ? exposed : null}
        </span>
      ) : null}
    </>
  );
}

/**
 * Une vignette, et son URL présignée.
 *
 * L'URL n'est demandée qu'**au montage**. La virtualisation démonte une tuile qui
 * sort de l'écran ; la mémoire de `thumbs.ts` fait que la remonter ne redemande
 * rien tant que l'URL vit, et le regroupement fait qu'une vague de montage ne coûte
 * qu'un appel.
 *
 * Une vignette absente n'est pas une panne de la page : la tuile reste, et elle
 * **dit pourquoi** quand le refus est définitif — un emplacement vide sans
 * explication se lit comme un bogue.
 */
export function Thumb({
  fid,
  alt,
  gid,
  strip,
}: {
  readonly fid: string;
  readonly alt: string;
  /**
   * **Un indice de résolution, jamais un droit** : il épargne au serveur de sonder
   * les groupes de l'appelant pour un `fid` qu'il ne possède pas. La décision reste
   * portée par l'arête — un `gid` faux ne donne accès à rien.
   */
  readonly gid?: string;
  /** La géométrie d'une pellicule, quand c'en est une. Voir `filmstripOf`. */
  readonly strip?: Filmstrip;
}): JSX.Element {
  const { src, failed } = useThumbUrl(fid, gid);
  const roll = useRollOnTouch();

  if (failed && !src) {
    return (
      <div class="tile-placeholder" data-testid={`unavailable-${fid}`} title={t('gallery.noThumb')}>
        <IconNoImage size={24} />
      </div>
    );
  }

  // **Une pellicule n'est pas une image de plus, c'est la même image fenêtrée.**
  // Une case fait 400 × 400 et tous nos emplacements de vignette sont carrés :
  // une planche de `frames` cases s'affiche donc en `frames × 100 %` de large,
  // et la case *i* se choisit par un `background-position-x` — 0 % pour la
  // première, 100 % pour la dernière. Aucun octet de plus, aucune requête de plus.
  if (src && strip) {
    return (
      <div
        class={roll.playing ? 'tile-image filmstrip rolling' : 'tile-image filmstrip'}
        data-testid={`thumb-${fid}`}
        role="img"
        aria-label={alt}
        // **Le doigt qui se pose déclenche la pellicule.** `pointerdown` et non
        // `touchstart` : le même événement couvre le doigt et le stylet, et il
        // porte `pointerType`, qui est exactement ce qui écarte la souris.
        onPointerDown={roll.start}
        onAnimationEnd={roll.done}
        style={{
          backgroundImage: `url("${src}")`,
          backgroundSize: `${strip.frames * 100}% 100%`,
          backgroundPositionX: `${positionOf(strip)}%`,
          // Le nombre de cases vient de la donnée : la boucle de survol doit donc
          // s'y accorder. `jump-none` est ce qui fait tomber les paliers **sur**
          // 0 % et 100 %, donc sur la première et la dernière case ; `steps(n)`
          // seul s'arrêterait avant la fin et montrerait des demi-cases.
          animationTimingFunction: `steps(${strip.frames}, jump-none)`,
        }}
      />
    );
  }

  return (
    <img
      class="tile-image"
      data-testid={`thumb-${fid}`}
      alt={alt}
      loading="lazy"
      decoding="async"
      src={src}
    />
  );
}

/**
 * **Ce qui remplace le survol là où il n'y a pas de souris** *(2026-08-16)*.
 *
 * **Le doigt qui se pose *est* le survol.** C'est le geste qu'on fait pour faire
 * défiler une grille : le doigt tombe sur une image, puis tire. La pellicule de
 * cette image-là joue alors une fois, exactement comme elle le ferait sous un
 * curseur — même déclencheur, même durée, même repos sur l'affiche ensuite.
 *
 * La première rédaction jouait à l'**entrée à l'écran**, sous observateur de
 * visibilité : c'était un choix de conception, pas une demande, et l'essai l'a
 * tranché. Un mouvement que personne n'a déclenché est un mouvement subi ; celui
 * -ci répond à un geste, donc il ne surprend pas, et il ne joue **que** sur la
 * tuile qu'on touche plutôt que sur toutes celles qui passent.
 *
 * Deux gardes suffisent, et elles tiennent en une ligne chacune :
 *
 * - **Ce n'est pas une souris** : un curseur a déjà `:hover`, et deux animations
 *   sur le même élément se marcheraient dessus.
 * - **Pas de relance en cours de route** : le doigt qui repart et revient
 *   pendant l'animation ne la redémarre pas au milieu.
 *
 * `prefers-reduced-motion` la coupe, comme le survol — c'est fait en CSS, à un
 * seul endroit pour les deux.
 */
function useRollOnTouch(): {
  readonly playing: boolean;
  readonly start: (ev: { readonly pointerType?: string }) => void;
  readonly done: () => void;
} {
  const [playing, setPlaying] = useState(false);
  return {
    playing,
    start: (ev) => {
      if (ev.pointerType === 'mouse') return;
      if (!playing) setPlaying(true);
    },
    // L'animation retirée, la case revient à la position **en ligne** — celle de
    // l'affiche. C'est ce qui fait que le repos est le choix du propriétaire.
    done: () => setPlaying(false),
  };
}

/**
 * **L'URL présignée d'une vignette**, et son refus éventuel.
 *
 * Extrait de `Thumb` le 2026-08-15, quand le sélecteur d'affiche a eu besoin de
 * la même image : la pellicule qu'on choisit est **exactement** celle qui est déjà
 * peinte dans la grille, et rien ne justifiait une seconde façon d'aller la
 * chercher. Le lot de `ThumbCache` la sert d'ailleurs sans un appel de plus.
 */
export function useThumbUrl(
  fid: string,
  gid?: string,
): { readonly src: string | undefined; readonly failed: string | undefined } {
  const [src, setSrc] = useState<string | undefined>(() => knownUrl(fid));
  const [failed, setFailed] = useState<string | undefined>(() => refusedUrl(fid));

  useEffect(() => {
    if (src) return;
    let alive = true;
    void urlFor(fid, 'thumb', gid)
      .then((url) => {
        if (alive) setSrc(url);
      })
      .catch((err) => {
        if (alive) setFailed(err instanceof Error ? err.message : 'unreadable');
      });
    return () => {
      alive = false;
    };
  }, [fid]);

  return { src, failed };
}

/** Une pellicule : combien de cases, et laquelle sert d'affiche. */
export interface Filmstrip {
  readonly frames: number;
  readonly frame: number;
}

/**
 * La géométrie de la pellicule d'un objet, ou **rien** — et « rien » est le cas
 * de toutes les photos : `frames` n'existe que sous la vignette d'une vidéo (M5).
 *
 * Une seule fonction sait lire la convention, parce qu'il y a quatre endroits qui
 * peignent une vignette et qu'ils doivent la lire pareil.
 */
export function filmstripOf(item: {
  readonly Variants?: Record<string, { frames?: number }>;
  readonly PosterFrame?: number | null;
}): Filmstrip | undefined {
  const frames = item.Variants?.['thumb']?.frames;
  if (frames === undefined || frames < 2) return undefined;
  // L'affiche est un entier borné à la donnée : une valeur hors planche —
  // héritée d'une vidéo remplacée, ou simplement fausse — montrerait du vide.
  const frame = Math.min(Math.max(item.PosterFrame ?? 0, 0), frames - 1);
  return { frames, frame };
}

/**
 * La position de la case, en pourcentage de `background-position-x`.
 *
 * Le pourcentage d'un `background-position` **n'est pas une fraction de
 * l'image** : c'est l'alignement du même point sur les deux, si bien que 100 %
 * cale le bord droit de la planche sur le bord droit de la fenêtre. La case *i*
 * est donc à `i / (frames - 1)`, pas à `i / frames` — et l'erreur d'un cran ne se
 * verrait que sur la dernière.
 */
export const positionOf = ({ frames, frame }: Filmstrip): number => (frame / (frames - 1)) * 100;
